import { matchRoutes, type RouteObject } from 'react-router-dom';
import { type AppRoute, routes as appRoutes } from '@web/core/navigation/app.navigator';
import { administrationRoutes } from '@web/administration/administration-routes';
import { bmisRoutes } from '@web/bmis/bmis-routes';
import { ModuleName } from '@web/core/navigation/module-name';
import { dmsRoutes } from '@web/vin-sessions/components/dms/dms-routes';
import { campaignsRoutes } from '@web/vin-sessions/components/campaigns/campaigns-routes';

type ChildRoute = { path?: string; index?: boolean; moduleName?: ModuleName };
type RouteHandle = { moduleName?: ModuleName; isRedirectOnly?: boolean };
export type ResolvedRoute = { pattern: string; moduleName: ModuleName | undefined; isRedirectOnly: boolean };

const NOT_FOUND_PATTERN = '/not-found';

const CHILD_ROUTERS: Array<{ mount: string; routes: readonly ChildRoute[] }> = [
    { mount: '/administration', routes: administrationRoutes },
    { mount: '/vin/:vin/bmis', routes: bmisRoutes },
    { mount: '/vin/:vin/repair-orders', routes: dmsRoutes },
    { mount: '/vin/:vin/campaigns', routes: campaignsRoutes },
];

const CHILD_ROUTER_MOUNT_PATTERNS = new Set(CHILD_ROUTERS.map((child) => `${child.mount}/*`));

function toRouteObject(path: string, moduleName: ModuleName | undefined, isRedirectOnly = false): RouteObject {
    const handle: RouteHandle = { moduleName, isRedirectOnly };
    return { path, handle };
}

function expandChildRoute(mount: string, route: ChildRoute): RouteObject | null {
    if (route.index) {
        return toRouteObject(mount, route.moduleName);
    }
    if (!route.path) {
        return null;
    }
    if (route.path === '*') {
        return toRouteObject(`${mount}/*`, ModuleName.NotFound);
    }
    const childPath = route.path.replace(/^\/+/, '');
    return toRouteObject(childPath ? `${mount}/${childPath}` : mount, route.moduleName);
}

function expandChildRoutes(): RouteObject[] {
    return CHILD_ROUTERS.flatMap(({ mount, routes }) =>
        routes.map((route) => expandChildRoute(mount, route)).filter((r): r is RouteObject => r !== null)
    );
}

function isRedirectOnly(route: AppRoute): boolean {
    return Boolean(route.redirectTo) && !route.main;
}

function fromAppRoute(route: AppRoute): RouteObject {
    return toRouteObject(route.path, route.moduleName, isRedirectOnly(route));
}

const routePatterns: RouteObject[] = [
    ...appRoutes.filter((r) => !CHILD_ROUTER_MOUNT_PATTERNS.has(r.path)).map(fromAppRoute),
    ...expandChildRoutes(),
];

function isCatchAllPattern(pattern: string): boolean {
    return pattern === '*' || pattern === '/*';
}

/**
 * Resolves a pathname to its declared route pattern, module name, and redirect status.
 *
 * Matches the pathname against app routes, returning the pattern declared in the route table
 * (e.g., `/qmrs/:displayIdentifier`, never the actual path `/qmrs/abc-123`), which ensures
 * GA4 groups similar pages together. Unmatched paths resolve to `/not-found`.
 */
export function resolvePageRoute(pathname: string): ResolvedRoute {
    const matches = matchRoutes(routePatterns, pathname);

    if (!matches || matches.length === 0) {
        return { pattern: NOT_FOUND_PATTERN, moduleName: ModuleName.NotFound, isRedirectOnly: false };
    }

    const match = matches[matches.length - 1];
    let pattern = match.route.path ?? NOT_FOUND_PATTERN;
    const handle = match.route.handle as RouteHandle | undefined;
    const moduleName = handle?.moduleName;
    const redirectOnly = Boolean(handle?.isRedirectOnly);

    if (isCatchAllPattern(pattern)) {
        return { pattern: NOT_FOUND_PATTERN, moduleName: ModuleName.NotFound, isRedirectOnly: false };
    }

    // Mount-scoped catch-all → `${mount}/not-found` so the URL hierarchy
    // is preserved in GA4 (analyst can distinguish 404s by namespace).
    if (moduleName === ModuleName.NotFound && pattern.endsWith('/*')) {
        const mount = pattern.slice(0, -2);
        return { pattern: `${mount}${NOT_FOUND_PATTERN}`, moduleName: ModuleName.NotFound, isRedirectOnly: false };
    }

    if (pattern.endsWith('/*')) {
        pattern = pattern.slice(0, -2) || '/';
    }

    return { pattern, moduleName, isRedirectOnly: redirectOnly };
}

/**
 * Resolves the referrer for a page view, preferring the previously reported URL for accurate referrer chains.
 *
 * Falls back to `document.referrer`: stripped to its origin if cross-origin, resolved to a route pattern
 * if same-origin and tracked, or `''` if none of these apply or the referrer is invalid/empty.
 */
export function resolvePageReferrer(previousUrl: string | null, rawReferrer: string, origin: string): string {
    if (previousUrl) {
        return previousUrl;
    }

    if (!rawReferrer) {
        return '';
    }

    let parsedReferrer: URL;
    try {
        parsedReferrer = new URL(rawReferrer);
    } catch {
        return '';
    }

    if (parsedReferrer.origin !== origin) {
        return parsedReferrer.origin;
    }

    const { pattern, moduleName } = resolvePageRoute(parsedReferrer.pathname);
    if (!moduleName || pattern === NOT_FOUND_PATTERN || pattern.endsWith(NOT_FOUND_PATTERN)) {
        return parsedReferrer.origin;
    }

    return `${parsedReferrer.origin}${pattern}`;
}
